import { useState } from 'react';

import toast from 'react-hot-toast';
import { Loader2, UserPlus, AlertCircle, Mail } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';

import { workspaceMembersApi } from '@/lib/api/workspace-members.api';
import type { WorkspaceRole } from '@optidev/shared-types';

interface InviteMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  memberLimit: number | null;
  currentCount: number;
}

/**
 * Invite Member Dialog
 * Lovable-style invite UI with multi-email support
 * Security: Never reveals if an email is already registered
 */
export function InviteMemberDialog({
  open,
  onOpenChange,
  onSuccess,
  memberLimit,
  currentCount,
}: InviteMemberDialogProps) {
  const [emails, setEmails] = useState('');
  const [role, setRole] = useState<WorkspaceRole>('user');
  const [loading, setLoading] = useState(false);
  const [successCount, setSuccessCount] = useState<number | null>(null);

  const isAtLimit = memberLimit !== null && currentCount >= memberLimit;

  // Parse comma-separated emails
  const parseEmails = (input: string): string[] => {
    return input
      .split(',')
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.length > 0);
  };

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailList = parseEmails(emails);

    if (emailList.length === 0) {
      toast.error('Please enter at least one email address');
      return;
    }

    // Validate all emails
    const invalidEmails = emailList.filter((e) => !isValidEmail(e));
    if (invalidEmails.length > 0) {
      toast.error(`Invalid email format: ${invalidEmails.join(', ')}`);
      return;
    }

    // Check member limit
    if (memberLimit !== null && currentCount + emailList.length > memberLimit) {
      toast.error(
        `Cannot invite ${emailList.length} members. Limit: ${memberLimit - currentCount} remaining.`,
      );
      return;
    }

    try {
      setLoading(true);
      let successfulInvites = 0;
      const errors: string[] = [];

      // Invite each email sequentially
      for (const email of emailList) {
        try {
          await workspaceMembersApi.inviteMember(email, role);
          successfulInvites++;
        } catch (error: unknown) {
          const err = error as { response?: { data?: { message?: string } } };
          errors.push(`${email}: ${err.response?.data?.message || 'Failed'}`);
        }
      }

      if (successfulInvites > 0) {
        setSuccessCount(successfulInvites);
        onSuccess();

        // Close after showing success
        setTimeout(() => {
          handleClose();
        }, 2000);
      }

      if (errors.length > 0) {
        toast.error(errors.join('\n'));
      } else {
        toast.success(
          successfulInvites === 1
            ? 'Invitation sent'
            : `${successfulInvites} invitations sent`,
        );
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to send invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmails('');
    setRole('user');
    setSuccessCount(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Invite Member</DialogTitle>
          <DialogDescription>
            Enter one or more email addresses, separating multiple addresses
            with commas.
          </DialogDescription>
        </DialogHeader>

        {isAtLimit ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              You've reached the member limit for your plan ({currentCount}/
              {memberLimit}). Upgrade to invite more members.
            </AlertDescription>
          </Alert>
        ) : successCount !== null ? (
          <Alert>
            <Mail className="h-4 w-4" />
            <AlertDescription>
              {successCount === 1
                ? 'Invitation sent successfully.'
                : `${successCount} invitations sent successfully.`}
            </AlertDescription>
          </Alert>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex gap-2">
              <Input
                id="emails"
                type="text"
                placeholder="name@example.com, name2@example.com"
                value={emails}
                onChange={(e) => setEmails(e.target.value)}
                className="flex-1"
                autoFocus
              />
              <Button type="submit" disabled={loading} variant="ghost" className="h-9 px-3 bg-foreground/[0.07] hover:bg-foreground/[0.12] text-foreground/80 hover:text-foreground">
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Add'
                )}
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={role}
                onValueChange={(v) => setRole(v as WorkspaceRole)}
              >
                <SelectTrigger
                  id="role"
                  className="h-auto min-h-[52px] pt-1.5 pb-2 whitespace-normal [&>span]:line-clamp-none"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">
                    <div className="flex flex-col items-start">
                      <span>Admin</span>
                      <span className="text-xs text-muted-foreground">
                        Can manage members and settings
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="user">
                    <div className="flex flex-col items-start">
                      <span>User</span>
                      <span className="text-xs text-muted-foreground">
                        Can create and edit projects
                      </span>
                    </div>
                  </SelectItem>
                  <SelectItem value="viewer">
                    <div className="flex flex-col items-start">
                      <span>Viewer</span>
                      <span className="text-xs text-muted-foreground">
                        Can view projects only
                      </span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
